import { DOCUMENT, NgOptimizedImage } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  computed,
  inject,
  signal,
  type ElementRef,
  viewChild,
} from '@angular/core';
import { NavigationEnd, Router, RouterLink, RouterOutlet } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NgIcon, provideIcons } from '@ng-icons/core';
import {
  heroArrowRightOnRectangle,
  heroArrowPath,
  heroBanknotes,
  heroBars3,
  heroBeaker,
  heroBell,
  heroBookOpen,
  heroBolt,
  heroBuildingOffice2,
  heroCalendarDays,
  heroChartBarSquare,
  heroChatBubbleLeftRight,
  heroChevronDown,
  heroCheckCircle,
  heroCircleStack,
  heroCog6Tooth,
  heroCommandLine,
  heroComputerDesktop,
  heroCreditCard,
  heroCpuChip,
  heroClock,
  heroDocumentArrowUp,
  heroDocumentText,
  heroExclamationTriangle,
  heroFlag,
  heroFunnel,
  heroIdentification,
  heroKey,
  heroLink,
  heroListBullet,
  heroMagnifyingGlass,
  heroMegaphone,
  heroMoon,
  heroPhone,
  heroPlayCircle,
  heroQueueList,
  heroRectangleGroup,
  heroRectangleStack,
  heroRocketLaunch,
  heroShieldCheck,
  heroShoppingBag,
  heroSlash,
  heroSparkles,
  heroSquares2x2,
  heroSun,
  heroTag,
  heroUser,
  heroUserGroup,
  heroUsers,
  heroWrenchScrewdriver,
  heroXMark,
} from '@ng-icons/heroicons/outline';
import { lucideSidebarClose, lucideSidebarOpen } from '@ng-icons/lucide';
import { NgpMenuTrigger } from 'ng-primitives/menu';
import { filter, switchMap, tap } from 'rxjs';
import { AuthStore } from '../../core/auth/auth.store';
import {
  APP_MODULE_GROUPS,
  SETTINGS_MODULES,
  SETTINGS_NAV_ITEM,
  type AppModuleNavItem,
} from '../../core/navigation/app-modules';
import { HeaderSearchService } from '../../core/search/header-search.service';
import { SettingsCoreApiService, type CoreMeBusiness } from '../../features/settings/settings-core-api.service';
import { ToastrService, UiBackdropButton, UiIconButton, UiInput, UiMenuItem, UiMenuPanel, UiMenuSeparator } from '../../shared/ui';

type CommandItem = AppModuleNavItem & {
  keywords: string;
  parentLabel?: string;
};

type ThemeMode = 'light' | 'dark' | 'system';

type ThemePulse = {
  color: string;
  x: number;
  y: number;
};

const THEME_STORAGE_KEY = 'automato-theme-mode';
const SIDEBAR_OPEN_GROUPS_STORAGE_KEY = 'automato-sidebar-open-groups';
const THEME_SEQUENCE: readonly ThemeMode[] = ['light', 'dark', 'system'];
const THEME_LABELS: Record<ThemeMode, string> = {
  light: 'Claro',
  dark: 'Escuro',
  system: 'Sistema',
};
const THEME_ICONS: Record<ThemeMode, string> = {
  light: 'heroSun',
  dark: 'heroMoon',
  system: 'heroComputerDesktop',
};
const THEME_BASE_COLORS: Record<'light' | 'dark', string> = {
  light: 'var(--color-bg)',
  dark: 'var(--color-bg)',
};

@Component({
  selector: 'app-shell',
  host: {
    '(document:keydown)': 'handleGlobalKeydown($event)',
  },
  imports: [
    NgOptimizedImage,
    NgIcon,
    NgpMenuTrigger,
    RouterLink,
    RouterOutlet,
    UiIconButton,
    UiBackdropButton,
    UiInput,
    UiMenuItem,
    UiMenuPanel,
    UiMenuSeparator,
  ],
  providers: [
    provideIcons({
      heroArrowRightOnRectangle,
      heroArrowPath,
      heroBanknotes,
      heroBars3,
      heroBeaker,
      heroBell,
      heroBookOpen,
      heroBolt,
      heroBuildingOffice2,
      heroCalendarDays,
      heroChartBarSquare,
      heroChatBubbleLeftRight,
      heroChevronDown,
      heroCheckCircle,
      heroCircleStack,
      heroCog6Tooth,
      heroCommandLine,
      heroComputerDesktop,
      heroCreditCard,
      heroCpuChip,
      heroClock,
      heroDocumentArrowUp,
      heroDocumentText,
      heroExclamationTriangle,
      heroFlag,
      heroFunnel,
      heroIdentification,
      heroKey,
      heroLink,
      heroListBullet,
      heroMagnifyingGlass,
      heroMegaphone,
      heroMoon,
      heroPhone,
      heroPlayCircle,
      heroQueueList,
      heroRectangleGroup,
      heroRectangleStack,
      heroRocketLaunch,
      heroShieldCheck,
      heroShoppingBag,
      heroSlash,
      heroSparkles,
      heroSquares2x2,
      heroSun,
      heroTag,
      heroUser,
      heroUserGroup,
      heroUsers,
      heroWrenchScrewdriver,
      heroXMark,
      lucideSidebarClose,
      lucideSidebarOpen,
    }),
  ],
  templateUrl: './app-shell.html',
  styleUrl: './app-shell.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppShellComponent {
  private readonly document = inject(DOCUMENT);
  private readonly destroyRef = inject(DestroyRef);
  private readonly authStore = inject(AuthStore);
  private readonly settingsCoreApi = inject(SettingsCoreApiService);
  private readonly router = inject(Router);
  private readonly toastr = inject(ToastrService);
  private readonly headerSearch = inject(HeaderSearchService);

  readonly commandInput = viewChild<ElementRef<HTMLInputElement>>('commandInput');
  readonly sidebarCollapsed = signal(false);
  readonly mobileSidebarOpen = signal(false);
  readonly currentUrl = signal(this.router.url);
  readonly openNavGroups = signal<readonly string[]>(readStoredNavGroups(this.document));
  readonly commandPaletteOpen = signal(false);
  readonly commandQuery = signal('');
  readonly availableBusinesses = signal<CoreMeBusiness[]>([]);
  readonly businessSwitcherLoading = signal(false);
  readonly switchingBusinessId = signal<string | null>(null);
  readonly themeMode = signal<ThemeMode>(readStoredThemeMode(this.document));
  readonly resolvedTheme = signal<'light' | 'dark'>(this.resolveTheme(this.themeMode()));
  readonly themePulse = signal<ThemePulse | null>(null);
  readonly logoSrc = computed(() =>
    this.resolvedTheme() === 'dark'
      ? '/Cópia de Automato. (Capa para Facebook)(1).svg'
      : '/Cópia de Automato. (Capa para Facebook).svg',
  );
  readonly markSrc = computed(() => (this.resolvedTheme() === 'dark' ? '/2.svg' : '/1.svg'));
  readonly businessName = computed(() => this.authStore.business()?.name ?? 'Automato');
  readonly currentBusinessId = computed(() => this.authStore.business()?.id ?? null);
  readonly businessInitials = computed(() => initialsFromName(this.businessName()));
  readonly userName = computed(() => this.authStore.user()?.name ?? 'Usuário');
  readonly userEmail = computed(() => this.authStore.user()?.email ?? '');
  readonly userAvatarUrl = computed(() => this.authStore.user()?.avatarUrl ?? null);
  readonly userInitials = computed(() => initialsFromName(this.userName()));
  readonly sidebarToggleLabel = computed(() => (this.sidebarCollapsed() ? 'Expandir navegação' : 'Recolher navegação'));
  readonly sidebarToggleIcon = computed(() => (this.sidebarCollapsed() ? 'lucideSidebarOpen' : 'lucideSidebarClose'));
  readonly themeLabel = computed(() => `Tema: ${THEME_LABELS[this.themeMode()]}`);
  readonly themeIcon = computed(() => THEME_ICONS[this.themeMode()]);
  readonly themeModes = THEME_SEQUENCE;
  readonly headerSearchContext = this.headerSearch.context;
  readonly headerSearchValue = this.headerSearch.value;

  readonly navGroups = computed(() => APP_MODULE_GROUPS);
  readonly settingsNavItem = computed(() => SETTINGS_NAV_ITEM);
  readonly commandItems = computed<readonly CommandItem[]>(() => {
    const appItems = this.navGroups().flatMap((group) => buildCommandItems(group));
    const settingsItem = this.settingsNavItem();

    return [...appItems, ...buildCommandItems([settingsItem]), ...buildCommandItems(SETTINGS_MODULES, settingsItem.label)];
  });
  readonly filteredCommandItems = computed(() => {
    const query = normalizeSearchText(this.commandQuery());

    if (!query) {
      return this.commandItems();
    }

    return this.commandItems().filter((item) => item.keywords.includes(query));
  });

  constructor() {
    this.applyTheme(this.themeMode());
    this.watchSystemTheme();
    this.loadBusinesses();
    this.syncOpenGroupWithCurrentUrl(this.router.url);
    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((event) => {
        this.currentUrl.set(event.urlAfterRedirects);
        this.mobileSidebarOpen.set(false);
        this.syncOpenGroupWithCurrentUrl(event.urlAfterRedirects);
      });
  }

  toggleSidebar() {
    this.sidebarCollapsed.update((collapsed) => !collapsed);
  }

  openMobileSidebar() {
    this.sidebarCollapsed.set(false);
    this.mobileSidebarOpen.set(true);
  }

  closeMobileSidebar() {
    this.mobileSidebarOpen.set(false);
  }

  toggleNavGroup(item: AppModuleNavItem) {
    if (!this.hasVisibleChildren(item)) {
      return;
    }

    this.openNavGroups.update((openGroups) => {
      const nextGroups = openGroups.includes(item.path) ? [] : [item.path];

      this.persistOpenNavGroups(nextGroups);
      return nextGroups;
    });
  }

  openNavGroup(item: AppModuleNavItem) {
    if (!this.hasVisibleChildren(item)) {
      return;
    }

    const nextGroups = [item.path];
    this.openNavGroups.set(nextGroups);
    this.persistOpenNavGroups(nextGroups);
  }

  hasVisibleChildren(item: AppModuleNavItem): boolean {
    return (item.children?.length ?? 0) > 0;
  }

  hasMainRouteChild(item: AppModuleNavItem): boolean {
    return item.children?.some((child) => child.path === item.path) ?? false;
  }

  isNavGroupExpanded(item: AppModuleNavItem): boolean {
    if (this.sidebarCollapsed()) {
      return false;
    }

    return this.openNavGroups().includes(item.path);
  }

  isNavItemActive(item: AppModuleNavItem): boolean {
    return this.isNavItemDirectlyActive(item) || (item.children?.some((child) => this.isNavItemActive(child)) ?? false);
  }

  isNavItemDirectlyActive(item: AppModuleNavItem): boolean {
    return isRouteActive(this.currentUrl(), item.path, item.exact);
  }

  cycleTheme(event: MouseEvent) {
    const current = this.themeMode();
    const nextMode = THEME_SEQUENCE[(THEME_SEQUENCE.indexOf(current) + 1) % THEME_SEQUENCE.length];

    this.themeMode.set(nextMode);
    this.persistTheme(nextMode);
    this.applyTheme(nextMode);
    this.playThemePulse(event, this.resolveTheme(nextMode));
  }

  setTheme(mode: ThemeMode, event?: MouseEvent) {
    if (this.themeMode() === mode) {
      return;
    }

    this.themeMode.set(mode);
    this.persistTheme(mode);
    this.applyTheme(mode);

    if (event) {
      this.playThemePulse(event, this.resolveTheme(mode));
    }
  }

  openCommandPalette() {
    this.commandPaletteOpen.set(true);
    window.setTimeout(() => this.commandInput()?.nativeElement.focus());
  }

  closeCommandPalette() {
    this.commandPaletteOpen.set(false);
    this.commandQuery.set('');
  }

  setCommandQuery(event: Event) {
    const input = event.target;

    if (input instanceof HTMLInputElement) {
      this.commandQuery.set(input.value);
    }
  }

  setHeaderSearchValue(event: Event) {
    const input = event.target;

    if (input instanceof HTMLInputElement) {
      this.headerSearch.setValue(input.value);
    }
  }

  selectCommand(item: CommandItem) {
    this.closeCommandPalette();
    void this.router.navigateByUrl(item.path);
  }

  handleCommandKeydown(event: KeyboardEvent) {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.closeCommandPalette();
      return;
    }

    if (event.key === 'Enter') {
      const firstItem = this.filteredCommandItems()[0];

      if (firstItem) {
        event.preventDefault();
        this.selectCommand(firstItem);
      }
    }
  }

  handleGlobalKeydown(event: KeyboardEvent) {
    if (event.key === 'Escape' && this.commandPaletteOpen()) {
      event.preventDefault();
      this.closeCommandPalette();
      return;
    }

    if (event.key === 'Escape' && this.mobileSidebarOpen()) {
      event.preventDefault();
      this.closeMobileSidebar();
      return;
    }

    const isCommandShortcut = (event.metaKey || event.ctrlKey) && event.key.toLocaleLowerCase('pt-BR') === 'k';

    if (!isCommandShortcut) {
      return;
    }

    event.preventDefault();
    this.openCommandPalette();
  }

  logout() {
    this.authStore.logout().subscribe();
  }

  loadBusinesses() {
    this.businessSwitcherLoading.set(true);
    this.settingsCoreApi
      .listBusinesses()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ({ businesses }) => {
          this.availableBusinesses.set(businesses);
          this.businessSwitcherLoading.set(false);
        },
        error: () => {
          this.businessSwitcherLoading.set(false);
        },
      });
  }

  switchBusiness(business: CoreMeBusiness) {
    const currentBusinessId = this.authStore.business()?.id;
    if (business.id === currentBusinessId) return;

    if (business.membership?.status !== 'active') {
      this.toastr.error('Você não tem acesso ativo a este business.');
      return;
    }

    this.switchingBusinessId.set(business.id);
    this.settingsCoreApi
      .switchBusiness(business.id)
      .pipe(
        tap(({ currentBusiness, currentMembership }) => {
          this.authStore.setCurrentBusiness(
            {
              id: currentBusiness.id,
              name: currentBusiness.name,
              status: currentBusiness.status,
              providerOrganizationId: currentBusiness.providerOrganizationId ?? null,
            },
            currentMembership.id,
          );
        }),
        switchMap(() => this.authStore.loadSession()),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: () => {
          this.switchingBusinessId.set(null);
          this.toastr.success('Business alternado.');
          void this.router.navigateByUrl('/dashboard');
        },
        error: () => {
          this.switchingBusinessId.set(null);
          this.toastr.error('Não foi possível alternar o business.');
        },
      });
  }

  businessSwitcherDisabled(business: CoreMeBusiness) {
    return this.switchingBusinessId() !== null || business.membership?.status !== 'active' || business.id === this.currentBusinessId();
  }

  businessAccessLabel(business: CoreMeBusiness) {
    if (business.id === this.currentBusinessId()) return 'Atual';
    return business.membership?.status === 'active' ? 'Disponível' : 'Sem acesso ativo';
  }

  themeModeLabel(mode: ThemeMode) {
    return THEME_LABELS[mode];
  }

  themeModeIcon(mode: ThemeMode) {
    return THEME_ICONS[mode];
  }

  private persistOpenNavGroups(paths: readonly string[]) {
    this.document.defaultView?.localStorage.setItem(SIDEBAR_OPEN_GROUPS_STORAGE_KEY, JSON.stringify(paths));
  }

  private syncOpenGroupWithCurrentUrl(url: string) {
    const activeGroupPath = findActiveExpandablePath(url, APP_MODULE_GROUPS);
    const nextGroups = activeGroupPath ? [activeGroupPath] : [];
    const currentGroups = this.openNavGroups();

    if (currentGroups.length === nextGroups.length && currentGroups.every((path, index) => path === nextGroups[index])) {
      return;
    }

    this.openNavGroups.set(nextGroups);
    this.persistOpenNavGroups(nextGroups);
  }

  private applyTheme(mode: ThemeMode) {
    const resolvedTheme = this.resolveTheme(mode);
    const root = this.document.documentElement;

    this.resolvedTheme.set(resolvedTheme);
    root.dataset['themeMode'] = mode;
    root.dataset['theme'] = resolvedTheme;
    root.style.colorScheme = resolvedTheme;
  }

  private persistTheme(mode: ThemeMode) {
    this.document.defaultView?.localStorage.setItem(THEME_STORAGE_KEY, mode);
  }

  private resolveTheme(mode: ThemeMode): 'light' | 'dark' {
    if (mode !== 'system') {
      return mode;
    }

    return this.document.defaultView?.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }

  private playThemePulse(event: MouseEvent, resolvedTheme: 'light' | 'dark') {
    if (this.document.defaultView?.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return;
    }

    const target = event.currentTarget;
    if (!(target instanceof HTMLElement)) {
      return;
    }

    const rect = target.getBoundingClientRect();
    this.themePulse.set({
      color: THEME_BASE_COLORS[resolvedTheme],
      x: rect.left + rect.width / 2,
      y: rect.top + rect.height / 2,
    });
    this.document.defaultView?.setTimeout(() => this.themePulse.set(null), 720);
  }

  private watchSystemTheme() {
    const mediaQuery = this.document.defaultView?.matchMedia('(prefers-color-scheme: dark)');

    if (!mediaQuery) {
      return;
    }

    const listener = () => {
      if (this.themeMode() === 'system') {
        this.applyTheme('system');
      }
    };

    mediaQuery.addEventListener('change', listener);
    this.destroyRef.onDestroy(() => mediaQuery.removeEventListener('change', listener));
  }

}

function initialsFromName(name: string) {
  const initials = name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0]?.toLocaleUpperCase('pt-BR') ?? '')
    .join('');

  return initials || 'AU';
}

function normalizeSearchText(value: string) {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLocaleLowerCase('pt-BR');
}

function buildCommandItems(items: readonly AppModuleNavItem[], parentLabel?: string): readonly CommandItem[] {
  return items.flatMap((item) => {
    const commandItem: CommandItem = {
      ...item,
      ...(parentLabel ? { parentLabel } : {}),
      keywords: normalizeSearchText(`${parentLabel ?? ''} ${item.label} ${item.eyebrow} ${item.description} ${item.path}`),
    };

    return [commandItem, ...(item.children ? buildCommandItems(item.children, item.label) : [])];
  });
}

function findActiveExpandablePath(currentUrl: string, groups: readonly (readonly AppModuleNavItem[])[]): string | null {
  for (const group of groups) {
    for (const item of group) {
      if (item.children?.length && isNavTreeRouteActive(currentUrl, item)) {
        return item.path;
      }
    }
  }

  return null;
}

function isNavTreeRouteActive(currentUrl: string, item: AppModuleNavItem): boolean {
  return isRouteActive(currentUrl, item.path, item.exact) || (item.children?.some((child) => isNavTreeRouteActive(currentUrl, child)) ?? false);
}

function isRouteActive(currentUrl: string, itemPath: string, exact: boolean) {
  const currentPath = normalizePath(currentUrl);
  const targetPath = normalizePath(itemPath);

  if (exact) {
    return currentPath === targetPath;
  }

  return currentPath === targetPath || currentPath.startsWith(`${targetPath}/`);
}

function normalizePath(url: string) {
  const path = url.split(/[?#]/)[0] || '/';
  return path.endsWith('/') && path.length > 1 ? path.slice(0, -1) : path;
}

function readStoredThemeMode(document: Document): ThemeMode {
  const storedMode = document.defaultView?.localStorage.getItem(THEME_STORAGE_KEY);

  return storedMode === 'light' || storedMode === 'dark' || storedMode === 'system' ? storedMode : 'system';
}

function readStoredNavGroups(document: Document): readonly string[] {
  const storedGroups = document.defaultView?.localStorage.getItem(SIDEBAR_OPEN_GROUPS_STORAGE_KEY);

  if (!storedGroups) {
    return [];
  }

  try {
    const parsedGroups: unknown = JSON.parse(storedGroups);
    return Array.isArray(parsedGroups) ? parsedGroups.filter((path): path is string => typeof path === 'string') : [];
  } catch {
    return [];
  }
}
